import React from 'react'
// libs
import Chip from '@mui/material/Chip'
import { useMutation } from '@apollo/client/react'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// hooks
import { useFormsLinking } from 'hooks'
// types
import { IRisk, IRiskResponse } from 'types/graphql'
// views
import { ChipContent, Wrapper } from './views'
// graphql
import { RISK_CLONE_MUTATION } from 'graphql/risk/mutations'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type RiskCloneResponse = { riskClone: IRiskResponse }

const LinkingControl: React.FC = () => {
  const { isLinking, onAddClick, hideModal } = useFormsLinking()

  const [cloneRisk] = useMutation<RiskCloneResponse>(RISK_CLONE_MUTATION)

  const [isLoading, setIsLoading] = React.useState(false)

  const { fieldLinkId } = useFormsLinking()

  const { risksToLink, setRisksToLink } = React.useContext(RiskViewAPIContext)
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)
  const { risk: currentRisk, riskRefetch, refetchLinkedRisksPermissions } = React.useContext(RiskAPIContext)

  const copyRisk = async (riskId: string) => {
    try {
      const {
        data: {
          riskClone: { data }
        }
      } = await cloneRisk({
        variables: {
          id: riskId,
          input: {
            fieldLinkId,
            parentRiskId: currentRisk?.id,
            isLinkedRiskDefault: isRiskTemplate
          }
        }
      })

      await onAddClick(data as IRisk)
    } catch (e) {
      console.log('[LinkingControl.copyRisk]: ', parseGraphQLErrorMessages(e))
    }
  }

  const onLinkClick = async () => {
    setIsLoading(true)

    for (const riskToLink of risksToLink) {
      if (riskToLink.action === 'copy') {
        await copyRisk(riskToLink.id)
      } else {
        await onAddClick(riskToLink)
      }
    }

    await refetchLinkedRisksPermissions?.()
    await riskRefetch?.()

    setIsLoading(false)
    hideModal()
  }

  if (!isLinking) {
    return null
  }

  return (
    <Wrapper>
      {!!risksToLink.length && (
        <ChipContent>
          <Chip label={risksToLink.length} onDelete={() => setRisksToLink([])} />
          {`item${risksToLink.length > 1 ? 's' : ''} selected`}
        </ChipContent>
      )}
      <Button
        isLoading={isLoading}
        disabled={!risksToLink.length}
        shadowLess
        onClick={onLinkClick}
        layout={BUTTON_LAYOUTS.PURPLE}>
        Link
      </Button>
    </Wrapper>
  )
}

export default LinkingControl
